// Events Data and Eligibility Logic
// This file centralizes the tournament rules and age categories.

export const getAgeGroups = (tournamentYear) => ({
  'U19': { name: 'Junior Boys/Girls (U19)', bornAfter: tournamentYear - 18 },
  'U17': { name: 'Junior Boys/Girls (U17)', bornAfter: tournamentYear - 16 },
  'U15': { name: 'Sub-Junior Boys/Girls (U15)', bornAfter: tournamentYear - 14 },
  'U13': { name: 'Cadet Boys/Girls (U13)', bornAfter: tournamentYear - 12 },
  'U11': { name: 'Hopes Boys/Girls (U11)', bornAfter: tournamentYear - 10 },
  'U09': { name: 'Hopes Boys/Girls (U09)', bornAfter: tournamentYear - 8 },
  'V39': { name: 'Veterans (39+)', bornBefore: tournamentYear - 39 },
  'V49': { name: 'Veterans (49+)', bornBefore: tournamentYear - 49 },
  'V59': { name: 'Veterans (59+)', bornBefore: tournamentYear - 59 },
  'V64': { name: 'Veterans (64+)', bornBefore: tournamentYear - 64 }
});
export const getEventsForYear = (tournamentYear) => {
  const year = parseInt(tournamentYear, 10) || new Date().getFullYear();
  
  return [
    // --- Boys & Men's Events (Ascending Order) ---
    { id: 'hb09', name: 'Hopes Boys Singles (U9)', category: 'singles', gender: 'male', bornOnOrAfter: `${year - 8}-01-01` },
    { id: 'hb11', name: 'Hopes Boys Singles (U11)', category: 'singles', gender: 'male', bornOnOrAfter: `${year - 10}-01-01` },
    { id: 'cb13', name: 'Cadet Boys Singles (U13)', category: 'singles', gender: 'male', bornOnOrAfter: `${year - 12}-01-01` },
    { id: 'sbj15', name: 'Sub Junior Boys Singles (U15)', category: 'singles', gender: 'male', bornOnOrAfter: `${year - 14}-01-01` },
    { id: 'jb17', name: 'Junior Boys Singles (U17)', category: 'singles', gender: 'male', bornOnOrAfter: `${year - 16}-01-01` },
    { id: 'jb19', name: 'Junior Boys Singles (U19)', category: 'singles', gender: 'male', bornOnOrAfter: `${year - 18}-01-01` },
    { id: 'ms_open', name: "Men's Singles (Open)", category: 'singles', gender: 'male' },
    { id: 'vm39', name: "Veteran 39+ Men's Singles", category: 'singles', gender: 'male', bornBefore: `${year - 39}-12-31` },
    { id: 'vm49', name: "Veteran 49+ Men's Singles", category: 'singles', gender: 'male', bornBefore: `${year - 49}-12-31` },
    { id: 'vm59', name: "Veteran 59+ Men's Singles", category: 'singles', gender: 'male', bornBefore: `${year - 59}-12-31` },
    { id: 'vm64', name: "Veteran 64+ Men's Singles", category: 'singles', gender: 'male', bornBefore: `${year - 64}-12-31` },
    { id: 'md', name: "Men's Doubles", category: 'doubles', gender: 'male' },

    // --- Girls & Women's Events (Ascending Order) ---
    { id: 'hg09', name: 'Hopes Girls Singles (U9)', category: 'singles', gender: 'female', bornOnOrAfter: `${year - 8}-01-01` },
    { id: 'hg11', name: 'Hopes Girls Singles (U11)', category: 'singles', gender: 'female', bornOnOrAfter: `${year - 10}-01-01` },
    { id: 'cg13', name: 'Cadet Girls Singles (U13)', category: 'singles', gender: 'female', bornOnOrAfter: `${year - 12}-01-01` },
    { id: 'sjg15', name: 'Sub Junior Girls Singles (U15)', category: 'singles', gender: 'female', bornOnOrAfter: `${year - 14}-01-01` },
    { id: 'jg17', name: 'Junior Girls Singles (U17)', category: 'singles', gender: 'female', bornOnOrAfter: `${year - 16}-01-01` },
    { id: 'jg19', name: 'Junior Girls Singles (U19)', category: 'singles', gender: 'female', bornOnOrAfter: `${year - 18}-01-01` },
    { id: 'ws_open', name: "Women's Singles (Open)", category: 'singles', gender: 'female' },
    { id: 'vw39', name: "Veteran 39+ Women's Singles", category: 'singles', gender: 'female', bornBefore: `${year - 39}-12-31` },
    { id: 'vw49', name: "Veteran 49+ Women's Singles", category: 'singles', gender: 'female', bornBefore: `${year - 49}-12-31` },
    { id: 'vw59', name: "Veteran 59+ Women's Singles", category: 'singles', gender: 'female', bornBefore: `${year - 59}-12-31` },
    { id: 'vw64', name: "Veteran 64+ Women's Singles", category: 'singles', gender: 'female', bornBefore: `${year - 64}-12-31` },

    // --- Mixed & Other Events ---
    { id: 'mx', name: 'Mixed Doubles', category: 'doubles', gender: 'any' },
    { id: 'only_register', name: 'Only Registration (No Event Participation)', category: 'singles', gender: 'any' }
  ];
};

export const isEligibleForEvent = (event, dob, gender, tournamentYear) => {
  if (!dob || !gender) return false;
  
  // Only Registration option is eligible for all age groups and genders
  if (event.id === 'only_register') return true;

  const birthDate = new Date(dob);
  const birthYear = birthDate.getFullYear();
  const TY = parseInt(tournamentYear, 10) || new Date().getFullYear();
  const age = TY - birthYear;
  
  // 1. Gender Lock — every gendered event is restricted to its own gender
  if (event.gender !== 'any' && event.gender !== gender) return false;

  // 2. Veteran Restrictions
  const isVeteranPlayer = age >= 39;
  
  if (isVeteranPlayer) {
    // 2a. Strict Veteran Bracket logic — must play in the correct age tier
    if (event.id.startsWith('v')) {
      if (age >= 64) {
        if (event.id !== 'vm64' && event.id !== 'vw64') return false;
      } else if (age >= 59) {
        if (event.id !== 'vm59' && event.id !== 'vw59') return false;
      } else if (age >= 49) {
        if (event.id !== 'vm49' && event.id !== 'vw49') return false;
      } else if (age >= 39) {
        if (event.id !== 'vm39' && event.id !== 'vw39') return false;
      }
    }
    // 2b. Veterans cannot play in youth age-group singles
    if (event.bornOnOrAfter) return false;
    // Note: Open singles (ms_open/ws_open) and Doubles (md, mx) are allowed for veterans
  }

  // 4. Youth Age-Group Logic (bornOnOrAfter / bornBefore)
  if (event.bornOnOrAfter) {
    const minDate = new Date(event.bornOnOrAfter);
    if (birthDate < minDate) return false;
  }
  if (event.bornBefore) {
    const maxDate = new Date(event.bornBefore);
    if (birthDate > maxDate) return false;
  }

  return true;
};

export const getProfileCategoryForYear = (dob, gender, tournamentYear) => {
  if (!dob) return "N/A";
  const birthDate = new Date(dob);
  const birthYear = birthDate.getFullYear();
  const TY = parseInt(tournamentYear, 10) || new Date().getFullYear();
  const age = TY - birthYear;
  
  if (age >= 64) return "64+ Veteran";
  if (age >= 59) return "59+ Veteran";
  if (age >= 49) return "49+ Veteran";
  if (age >= 39) return "39+ Veteran";
  
  if (age <= 8) return "U9 Hopes";
  if (age <= 10) return "U11 Hopes";
  if (age <= 12) return "U13 Cadet";
  if (age <= 14) return "U15 Sub-Junior";
  if (age <= 16) return "U17 Junior";
  if (age <= 18) return "U19 Junior";
  
  return gender === 'male' ? "Men's Open" : "Women's Open";
};

// Legacy support for static imports
export const EVENTS = getEventsForYear(new Date().getFullYear());